import useTeethTracking from 'components/Patients/TreatmentTracker/TeethTracking/useTeethTracking'
import { TeethTrackingStatuses } from 'components/Patients/TreatmentTracker/txTrackerConsts'
import useTxTracker from 'components/Patients/TreatmentTracker/useTxTracker'
import { TreatmentTypes } from 'consts/treatmentConsts'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

/**
 * @param {Object} params
 * @param {Object} params.treatmentTrackerData - The grin scan treatmentTrackerData
 * @returns {{
 *   scanOverallTrackingStatus: string | null,
 *   txTrackerAnalysisMessage: string
 * }}
 */
export default ({ treatmentTrackerData }) => {
  const { t } = useTranslation()
  const { calculateTreatmentType } = useTxTracker()
  const { getToothTrackingStatus } = useTeethTracking()

  const { treatment } = useSelector(state => state.treatmentReducer)

  const scanParsedTxTrackerData = useMemo(() => JSON.parse(treatmentTrackerData || '{}'), [treatmentTrackerData])

  const treatmentType = useMemo(
    () =>
      calculateTreatmentType({
        treatmentType: treatment.type,
        provider: JSON.parse(treatment.txTrackerStls || '{}')?.provider
      }),
    [calculateTreatmentType, treatment]
  )

  const teethTrackingStatuses = useMemo(() => {
    if (!scanParsedTxTrackerData?.teeth) {
      return {}
    }

    return Object.entries(scanParsedTxTrackerData.teeth).reduce((acc, [toothNumber, toothData]) => {
      const updatedAcc = { ...acc }
      updatedAcc[toothNumber] = getToothTrackingStatus({
        toothData,
        treatmentType
      })
      return updatedAcc
    }, {})
  }, [scanParsedTxTrackerData, getToothTrackingStatus, treatmentType])

  const trackingStatusCounts = useMemo(
    () =>
      Object.values(teethTrackingStatuses).reduce((acc, toothTrackingStatus) => {
        const updatedAcc = { ...acc }
        updatedAcc[toothTrackingStatus] = (acc[toothTrackingStatus] || 0) + 1
        return updatedAcc
      }, {}),
    [teethTrackingStatuses]
  )

  const scanOverallTrackingStatus = useMemo(() => {
    if (!Object.keys(trackingStatusCounts).length) {
      return null
    }

    if (treatmentType === TreatmentTypes.Aligners) {
      if (trackingStatusCounts[TeethTrackingStatuses.NotTracking] > 0) {
        return TeethTrackingStatuses.NotTracking
      } else if (trackingStatusCounts[TeethTrackingStatuses.PartiallyTracking] > 0) {
        return TeethTrackingStatuses.PartiallyTracking
      } else {
        return TeethTrackingStatuses.Tracking
      }
    } else {
      if (trackingStatusCounts[TeethTrackingStatuses.Fast] > 0) {
        return TeethTrackingStatuses.Fast
      } else if (trackingStatusCounts[TeethTrackingStatuses.Moderate] > 0) {
        return TeethTrackingStatuses.Moderate
      } else if (trackingStatusCounts[TeethTrackingStatuses.Slow] > 0) {
        return TeethTrackingStatuses.Slow
      } else if (trackingStatusCounts[TeethTrackingStatuses.NoSignificantMovement] > 0) {
        return TeethTrackingStatuses.NoSignificantMovement
      } else {
        return TeethTrackingStatuses.NoSignificantMovement
      }
    }
  }, [trackingStatusCounts, treatmentType])

  const txTrackerAnalysisMessage = useMemo(() => {
    if (
      scanOverallTrackingStatus === TeethTrackingStatuses.Tracking ||
      trackingStatusCounts[scanOverallTrackingStatus] > 1
    ) {
      return t(
        `pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.txTracker.messages.${scanOverallTrackingStatus}`,
        { numberOfTeeth: trackingStatusCounts[scanOverallTrackingStatus] }
      )
    }

    return t(
      `pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.txTracker.messages.${scanOverallTrackingStatus}Single`
    )
  }, [scanOverallTrackingStatus, t, trackingStatusCounts])

  return {
    scanOverallTrackingStatus,
    txTrackerAnalysisMessage
  }
}
